import Link from "next/link";
import { type ImageSummary } from "../types";
import { ProductElement } from "./ProductElement";
import { collectionOrder, collectionStatement, collectionSlug } from "@/lib/collections";

// Curated shop (DESIGN.md §4.2, §3): products grouped into collection "shows", each with an
// exhibition-title header + curatorial statement, then a cardless image-forward grid.

type Group = { series: string; statement?: string; products: ImageSummary[] };

function groupByCollection(products: ImageSummary[]): Group[] {
	const map = new Map<string, ImageSummary[]>();
	for (const p of products) {
		const key = p.series ?? "Studies"; // ungrouped pieces fall under a quiet "Studies" heading
		(map.get(key) ?? map.set(key, []).get(key)!).push(p);
	}
	return [...map.entries()]
		.map(([series, items]) => ({ series, statement: collectionStatement(series), products: items }))
		.sort((a, b) => collectionOrder(a.series) - collectionOrder(b.series));
}

export const ProductList = ({ products }: { products: ImageSummary[] }) => {
	const groups = groupByCollection(products);
	let globalIndex = 0; // for image priority/eager on the very first few across all groups

	return (
		<div data-testid="ProductList" className="space-y-section">
			{groups.map((group) => (
				<section key={group.series} aria-label={group.series}>
					<header className="mb-10 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 border-b border-mg-black/10 pb-6">
						<div>
							<h2 className="font-serif text-title font-light text-mg-black">{group.series}</h2>
							{group.statement ? <p className="mt-3 max-w-prose text-ink-muted">{group.statement}</p> : null}
						</div>
						{collectionSlug(group.series) ? (
							<Link
								href={`/collections/${collectionSlug(group.series)}`}
								className="label-caps shrink-0 text-ink-faint underline decoration-mg-gold-deep/40 underline-offset-4 transition-colors hover:text-mg-black"
							>
								The collection →
							</Link>
						) : null}
					</header>
					<ul role="list" className="grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
						{group.products.map((product) => {
							const i = globalIndex++;
							return (
								<ProductElement
									key={product.img_name}
									product={product}
									priority={i === 0}
									loading={i < 3 ? "eager" : "lazy"}
								/>
							);
						})}
					</ul>
				</section>
			))}
		</div>
	);
};
